import { useState, useEffect, useCallback } from 'react'
import { enqueueSnackbar } from 'notistack'
import useCaptureCamera from 'views/DigitalTrayMapping/CaptureTray/CaptureCamera/CaptureCamera.logic'

import useSubmitTrayCapture from 'views/DigitalTrayMapping/CaptureTray/CaptureCamera/SubmitTrayCapture.logic'
import { useGeneratePresignedUrlMutation } from 'lib/apollo/hooks'
import { uploadImageToS3 } from 'lib/utils/uploadToS3'
// import { handCheck } from 'lib/services/restApi/tracking'
import { getAnalysisJSONFileName } from 'lib/utils/getAnalysisJSONFile'
import { getScrewSizeFromProduct } from 'lib/utils/csvProductMapping'
import {
  ModalMode,
  ScanMode,
  ScannedProductDetails,
  ContinuousScannedProduct,
  TrayDetectionResponse,
} from './UDIScannerModal.types'

interface UseUDIScannerModalLogicProps {
  open: boolean
  onClose: () => void
  onScan: (code: string) => void
  onTrayStateUpdate?: (response: TrayDetectionResponse) => void
  searchResult?: any
  isSearching?: boolean
  searchError?: Error
  referenceImageUrl?: string
  trayType?: string
}

export const useUDIScannerModalLogic = ({
  open,
  onClose,
  onScan,
  onTrayStateUpdate,
  searchResult,
  isSearching,
  searchError,
  trayType,
}: UseUDIScannerModalLogicProps) => {
  const [currentMode, setCurrentMode] = useState<ModalMode>('scanning')
  const [scanMode, setScanMode] = useState<ScanMode>('single')
  const [scannedProduct, setScannedProduct] =
    useState<ScannedProductDetails | null>(null)
  const [continuousScannedProducts, setContinuousScannedProducts] = useState<
    ContinuousScannedProduct[]
  >([])
  const [quantity, setQuantity] = useState(1)
  const [lastScanTime, setLastScanTime] = useState<number>(0)
  const [pendingUDI, setPendingUDI] = useState<string>('')
  const [isProcessingScan, setIsProcessingScan] = useState(false)

  const {
    handleSPDCaptureImage,
    image,
    setImage,
    videoRef,
    cameras,
    activeCamera,
    fetchCameraData,
    isCameraMenuOpen,
    handleSelectCamera,
    handleToggleCameraMenu,
    orientation,
    videoHeight,
  } = useCaptureCamera()

  const { generatePresignedUrl } = useGeneratePresignedUrlMutation()
  const { base64ToFile, currentTemplateData, updateTemplateData } =
    useSubmitTrayCapture()

  const handleUDIScan = useCallback(
    (code: string) => {
      if (isProcessingScan) return

      const now = Date.now()
      if (now - lastScanTime < 3000) {
        return
      }
      setLastScanTime(now)

      setIsProcessingScan(true)
      if (scanMode === 'single') {
        onScan(code)
      } else {
        setPendingUDI(code)
        onScan(code)
      }
    },
    [onScan, lastScanTime, scanMode, isProcessingScan]
  )

  // Handle search results for continuous scanning
  useEffect(() => {
    if (
      scanMode === 'continuous' &&
      pendingUDI &&
      searchResult &&
      !searchError &&
      !isSearching
    ) {
      const asset = searchResult.assets?.[0]
      if (asset) {
        const productDetails: ContinuousScannedProduct = {
          id: `${Date.now()}_${Math.random()}`,
          udi: asset.deviceId || '',
          productName: asset.deviceDescription || '',
          deviceId: asset.deviceId || '',
          lotNumber: searchResult.productionIdentifier?.lotBatch || '',
          expirationDate: searchResult.productionIdentifier?.expirationDate
            ? typeof searchResult.productionIdentifier.expirationDate ===
              'string'
              ? searchResult.productionIdentifier.expirationDate
              : searchResult.productionIdentifier.expirationDate
                  .toISOString()
                  .split('T')[0]
            : '',
          quantity: searchResult.assets?.[0]?.deviceCount || 1,
          manufacturer: asset.company?.name || '',
          screwSize:
            getScrewSizeFromProduct(asset.deviceId, asset.deviceId) ||
            undefined,
          scannedAt: new Date(),
        }

        setContinuousScannedProducts((prev) => [...prev, productDetails])

        enqueueSnackbar(
          `Product "${productDetails.productName}" added to scan list`,
          { variant: 'success' }
        )
      }
      setPendingUDI('')

      setTimeout(() => {
        setIsProcessingScan(false)
      }, 1500)
    } else if (
      scanMode === 'continuous' &&
      pendingUDI &&
      searchError &&
      !isSearching
    ) {
      const errorProduct: ContinuousScannedProduct = {
        id: `error_${Date.now()}_${Math.random()}`,
        udi: pendingUDI,
        productName: 'Unknown Product',
        deviceId: pendingUDI,
        scannedAt: new Date(),
        error: searchError?.message || 'Product not found',
      }

      setContinuousScannedProducts((prev) => [...prev, errorProduct])
      enqueueSnackbar(`Failed to find product: ${pendingUDI}`, {
        variant: 'error',
      })
      setPendingUDI('')

      setTimeout(() => {
        setIsProcessingScan(false)
      }, 1500)
    }
  }, [scanMode, pendingUDI, searchResult, searchError, isSearching])

  // Handle single scan quantity
  useEffect(() => {
    if (scanMode === 'single' && searchResult?.assets?.[0]?.deviceCount) {
      setQuantity(searchResult.assets[0].deviceCount)
    } else if (scanMode === 'single' && searchResult) {
      setQuantity(1)
    }
  }, [scanMode, searchResult])

  useEffect(() => {
    if (scanMode === 'single' && searchResult && !searchError && !isSearching) {
      const asset = searchResult.assets?.[0]
      if (asset) {
        const productDetails: ScannedProductDetails = {
          udi: asset.deviceId || '',
          productName: asset.deviceDescription || '',
          deviceId: asset.deviceId || '',
          lotNumber: searchResult.productionIdentifier?.lotBatch || '',
          expirationDate: searchResult.productionIdentifier?.expirationDate
            ? typeof searchResult.productionIdentifier.expirationDate ===
              'string'
              ? searchResult.productionIdentifier.expirationDate
              : searchResult.productionIdentifier.expirationDate
                  .toISOString()
                  .split('T')[0]
            : '',
          quantity: asset.deviceCount || quantity,
          manufacturer: asset.company?.name || '',
          screwSize:
            getScrewSizeFromProduct(asset.deviceId, asset.deviceId) ||
            undefined,
        }
        setScannedProduct(productDetails)
        setIsProcessingScan(false)
        setCurrentMode('product-details')
      }
    }
  }, [scanMode, searchResult, searchError, isSearching, quantity])

  useEffect(() => {
    if (open) {
      setCurrentMode('scanning')
      setScanMode('single')
      setScannedProduct(null)
      setContinuousScannedProducts([])
      setQuantity(1)
      setLastScanTime(0)
      setPendingUDI('')
      setIsProcessingScan(false)
    } else {
      setIsProcessingScan(false)
    }
  }, [open])

  useEffect(() => {
    const fetchTemplateData = async () => {
      if (!trayType) {
        updateTemplateData(null)
        return
      }
      try {
        const baseUrl = process.env.REACT_APP_DTM_COMPUTER_VISION_API
        if (!baseUrl) return
        const apiUrl = `${baseUrl.replace(/\/$/, '')}/api/tray-templates`
        const url = `${apiUrl}?tray_type=${encodeURIComponent(trayType)}`
        const response = await fetch(url)
        if (!response.ok) {
          updateTemplateData(null)
          return
        }
        const data = await response.json()
        // Avoid unnecessary state updates if data is unchanged
        const prev = currentTemplateData
        const prevKey = prev ? `${prev.folder_name}|${prev.tray_type}` : ''
        const nextKey = data ? `${data.folder_name}|${data.tray_type}` : ''
        if (prevKey !== nextKey) {
          updateTemplateData(data)
        }
      } catch (err) {
        updateTemplateData(null)
      }
    }

    fetchTemplateData()
  }, [trayType, updateTemplateData, currentTemplateData])

  const handleScanModeToggle = useCallback(() => {
    const newMode: ScanMode = scanMode === 'single' ? 'continuous' : 'single'
    setScanMode(newMode)

    if (newMode === 'continuous') {
      setCurrentMode('continuous-scanning')
      setContinuousScannedProducts([])
    } else {
      setCurrentMode('scanning')
      setScannedProduct(null)
    }
  }, [scanMode])

  const handleRemoveScannedProduct = useCallback((productId: string) => {
    setContinuousScannedProducts((prev) =>
      prev.filter((p) => p.id !== productId)
    )
  }, [])

  const handleFinishScanning = useCallback(() => {
    const validProducts = continuousScannedProducts.filter(
      (product) => !product.error
    )

    if (validProducts.length === 0) {
      enqueueSnackbar(
        'No valid products scanned. Please scan at least one product successfully.',
        {
          variant: 'warning',
        }
      )
      return
    }

    const totalScannedQuantity = validProducts.reduce(
      (sum, product) => sum + (product.quantity || 1),
      0
    )

    setQuantity(totalScannedQuantity)
    setCurrentMode('camera')
    fetchCameraData()
  }, [continuousScannedProducts, fetchCameraData])

  const handleProceedToCamera = useCallback(() => {
    setCurrentMode('camera')
    fetchCameraData()
  }, [fetchCameraData])

  const handleImageCapture = useCallback(
    (skipRotate?: boolean) => {
      handleSPDCaptureImage(skipRotate)
    },
    [handleSPDCaptureImage]
  )

  const handleConfirmImage = useCallback(async () => {
    if (!image) return

    // setCurrentMode('hand-checking')

    try {
      // Upload image to S3
      const file = base64ToFile(image, 'tray_image.jpg', 'image/jpeg')
      const key = `tray_images/${Date.now()}_${file.name}`
      const presignedUrl = await generatePresignedUrl(key)

      if (!presignedUrl) {
        throw new Error('Failed to generate presigned URL')
      }

      const imageUrl = await uploadImageToS3(file, presignedUrl, key)

      if (!imageUrl) {
        throw new Error('Failed to upload image to S3')
      }

      // COMMENTED OUT: Check for hands in the image
      // const handCheckResponse = await handCheck({ image_url: imageUrl })

      // if (!handCheckResponse.success || !handCheckResponse.data) {
      //   throw new Error(handCheckResponse.message || 'Hand check failed')
      // }

      // const { hand_confidence, asset_confidence } = handCheckResponse.data

      // if (hand_confidence > 0.2) {
      //   enqueueSnackbar(
      //     'Hands detected in image. Please retake the photo without hands in the frame.',
      //     { variant: 'warning' }
      //   )
      //   setCurrentMode('camera')
      //   return
      // }

      // if (asset_confidence <= 0.9) {
      //   enqueueSnackbar(
      //     'Asset confidence too low. Please ensure the tray is clearly visible and retake the photo.',
      //     { variant: 'warning' }
      //   )
      //   setCurrentMode('camera')
      //   return
      // }

      // Process image with computer vision API
      setCurrentMode('processing')

      const templateFilename = trayType
        ? getAnalysisJSONFileName(trayType as any)
        : 'Stryker_2.0MP'

      const processImageResponse = await fetch(
        `${process.env.REACT_APP_DTM_COMPUTER_VISION_API}/api/process_image`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            after_image_url: imageUrl,
            template_filename:
              currentTemplateData?.folder_name || templateFilename,
          }),
        }
      )

      if (!processImageResponse.ok) {
        throw new Error('Process image API failed')
      }

      const processImageData = await processImageResponse.json()

      if (
        processImageData.error &&
        processImageData.error !== 'no error found'
      ) {
        throw new Error(processImageData.error)
      }

      const trayAnalysis =
        processImageData.tray_analysis ||
        processImageData.result?.tray_analysis ||
        processImageData.result?.analysis_results ||
        []

      const enhancedResponse = {
        analysis_results: trayAnalysis,
        scannedProduct:
          scanMode === 'single' ? scannedProduct || undefined : undefined,
        continuousScannedProducts:
          scanMode === 'continuous' ? continuousScannedProducts : undefined,
        quantity,
        processImageData,
        processedImageUrl: imageUrl,
      }

      if (onTrayStateUpdate) {
        onTrayStateUpdate(enhancedResponse)
      }

      // Consider plates as a valid detection path as well
      const plateRegions =
        processImageData?.result?.regions?.filter(
          (region: any) => region.type === 'plate'
        ) || []
      const hasPlateAreas = plateRegions.some(
        (region: any) => Array.isArray(region.areas) && region.areas.length > 0
      )

      // If the parent handles tray state updates (EditTray), defer success/no-items snackbars
      if (onTrayStateUpdate) {
        // do nothing here; parent will notify based on screws/plates
      } else if (trayAnalysis.length > 0 || hasPlateAreas) {
        const message = (() => {
          if (scanMode === 'continuous') {
            const productsCount = continuousScannedProducts.filter(
              (p) => !p.error
            ).length
            return hasPlateAreas && trayAnalysis.length === 0
              ? `Tray updated! ${productsCount} product types (Total Qty: ${quantity}) loaded successfully. Plates detected.`
              : `Tray updated! ${productsCount} product types (Total Qty: ${quantity}) loaded successfully. ${trayAnalysis.length} items detected.`
          }
          if (scannedProduct) {
            return hasPlateAreas && trayAnalysis.length === 0
              ? `Tray updated! Product "${scannedProduct.productName}" (Qty: ${quantity}) loaded successfully. Plates detected.`
              : `Tray updated! Product "${scannedProduct.productName}" (Qty: ${quantity}) loaded successfully. ${trayAnalysis.length} items detected.`
          }
          return hasPlateAreas && trayAnalysis.length === 0
            ? `Tray updated! Plates detected and mapped to the tray.`
            : `Tray updated! ${trayAnalysis.length} items detected and mapped to the tray.`
        })()

        enqueueSnackbar(message, { variant: 'success' })
      } else {
        enqueueSnackbar(
          `Image processed successfully, but no items were detected in the tray. Please ensure items are clearly visible and try again.`,
          { variant: 'warning' }
        )
      }

      setCurrentMode('scanning')
      setScannedProduct(null)
      setContinuousScannedProducts([])
    } catch (error) {
      enqueueSnackbar(
        `Failed to process tray image: ${
          (error as Error).message
        }. Please try again.`,
        { variant: 'error' }
      )
      setCurrentMode('camera')
    }
  }, [
    image,
    scannedProduct,
    continuousScannedProducts,
    quantity,
    scanMode,
    onTrayStateUpdate,
    generatePresignedUrl,
    base64ToFile,
    trayType,
    currentTemplateData?.folder_name,
  ])

  const handleRetakeImage = useCallback(() => {
    setImage('')
    fetchCameraData()
  }, [setImage, fetchCameraData])

  const handleBackToScanning = useCallback(() => {
    if (scanMode === 'continuous') {
      setCurrentMode('continuous-scanning')
    } else {
      setCurrentMode('scanning')
      setScannedProduct(null)
    }
    onScan('')
  }, [scanMode, onScan])

  const handleQuantityChange = useCallback((newQuantity: number) => {
    setQuantity(Math.max(1, newQuantity))
  }, [])

  const handleClose = useCallback(() => {
    if (currentMode === 'processing' || currentMode === 'hand-checking') {
      return
    }
    onClose()
  }, [currentMode, onClose])

  return {
    currentMode,
    scanMode,
    scannedProduct,
    continuousScannedProducts,
    quantity,
    searchResult,
    isSearching,
    searchError,
    isProcessingScan,
    handleUDIScan,
    handleScanModeToggle,
    handleRemoveScannedProduct,
    handleFinishScanning,
    videoRef,
    cameras,
    activeCamera,
    isCameraMenuOpen,
    image,
    setImage,
    orientation,
    videoHeight,
    handleSelectCamera,
    handleToggleCameraMenu,
    handleImageCapture,
    handleProceedToCamera,
    handleConfirmImage,
    handleRetakeImage,
    handleBackToScanning,
    handleQuantityChange,
    handleClose,
  }
}
